import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onAnimationEnd, isClickedOutside } from '@theme/utilities';

const SESSION_STORAGE_KEY = 'complete-look-shown';

// Compteur distinct de celui des suggestions : la grille ne se montre qu'une
// fois par session, alors que la question du prénom accompagne chaque vêtement.
const LETTER_SESSION_KEY = 'complete-look-letter-asked';

// Laisse le temps au drawer panier (auto-open) et à l'animation d'ajout de se poser
// avant d'afficher la pop-up par-dessus.
const OPEN_DELAY = 600;

// Laisse le feedback « ajouté ✓ » du bouton visible avant de fermer la pop-up.
const CLOSE_AFTER_ADD_DELAY = 900;

/**
 * @typedef {object} CartItem
 * @property {number} variant_id
 * @property {number} product_id
 * @property {string} product_title
 * @property {string | null} variant_title
 * @property {string} [image]
 */

/**
 * Construit l'URL Shopify haute résolution de l'image actuellement affichée
 * sur une carte, pour l'agrandissement (lightbox) sans pixelisation. La CDN
 * Shopify ne suragrandit pas : width=1600 renvoie au plus la taille native.
 * @param {HTMLImageElement | null | undefined} img
 * @returns {string}
 */
function highResImageUrl(img) {
  const url = img?.currentSrc || img?.src;
  if (!url) return '';
  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.set('width', '1600');
    return parsed.toString();
  } catch {
    return url;
  }
}

/**
 * Sélection d'une taille sur une carte cross-sell : renseigne l'input caché
 * name="id" du formulaire de la carte (les pilules sont hors du <form>).
 * @param {Element} root - Conteneur des cartes (pop-up ou section fiche produit).
 * @param {Event} event
 * @returns {boolean} Vrai si l'événement concernait bien une pilule de taille.
 */
function applySizeSelection(root, event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-look-size')) return false;

  const card = input.closest('[data-look-product-id]');
  const hiddenVariantInput = card?.querySelector('input[name="id"]');
  if (hiddenVariantInput instanceof HTMLInputElement) {
    hiddenVariantInput.value = input.value;
  }

  // Met l'image de la carte à la couleur de la variante choisie (ex : Argent),
  // si cette variante a sa propre image.
  const variantImage = input.getAttribute('data-look-image');
  const cardImage = card?.querySelector('.complete-look__detail-image, .complete-look__media img');
  if (variantImage && cardImage instanceof HTMLImageElement) {
    cardImage.removeAttribute('srcset');
    cardImage.removeAttribute('sizes');
    cardImage.src = variantImage;
  }

  return true;
}

/**
 * Les cartes avec choix de taille gardent leur bouton désactivé tant
 * qu'aucune taille n'est sélectionnée.
 * @param {Element} root
 */
function syncAddButtonsState(root) {
  for (const card of root.querySelectorAll('[data-look-product-id]')) {
    const sizeInputs = card.querySelectorAll('input[data-look-size]');
    if (sizeInputs.length === 0) continue;

    const hasSelection = [...sizeInputs].some((input) => input instanceof HTMLInputElement && input.checked);
    const addButton = card.querySelector('button[name="add"]');
    if (addButton instanceof HTMLButtonElement) {
      addButton.disabled = !hasSelection;
    }
  }
}

/**
 * Masque les cartes des produits déjà au panier.
 * @param {Element} root
 * @param {{ items?: CartItem[] } | undefined} cart
 * @returns {number} Le nombre de cartes encore visibles.
 */
function hideProductsInCart(root, cart) {
  const cards = root.querySelectorAll('[data-look-product-id]');
  const productIdsInCart = new Set((cart?.items ?? []).map((item) => item.product_id));
  let visibleCount = 0;

  for (const card of cards) {
    if (!(card instanceof HTMLElement)) continue;
    const isInCart = productIdsInCart.has(Number(card.dataset.lookProductId));
    card.toggleAttribute('hidden', isInCart);
    if (!isInCart) visibleCount += 1;
  }

  return visibleCount;
}

/**
 * Pop-up de cross-sell affichée après l'ajout au panier d'un produit
 * de la collection déclencheuse (ou de n'importe quel produit si
 * aucune collection n'est configurée).
 *
 * Deux vagues de suggestions : refuser la première (✕, Échap, clic à
 * l'extérieur) fait glisser le contenu vers la seconde dans la même fenêtre ;
 * refuser la seconde ferme la pop-up. Un ajout depuis la pop-up la ferme
 * directement (offre acceptée).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [addedItem] - Confirmation row for the product that was just added.
 * @property {HTMLImageElement} [addedItemImage]
 * @property {HTMLElement} [addedItemTitle]
 * @property {HTMLElement} [addedItemVariant]
 * @property {HTMLDialogElement} [zoomDialog] - Lightbox d'agrandissement d'image.
 * @property {HTMLImageElement} [zoomImage]
 * @property {HTMLElement} [letterBlock] - Bloc lettre, masqué si le produit ajouté n'y est pas éligible.
 *
 * @extends {Component<Refs>}
 */
class CompleteLookComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {ReturnType<typeof setTimeout> | null} */
  #timeout = null;

  #previousScrollY = 0;

  #ownsScrollLock = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('change', this.#handleSizeChange);
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('click', this.#handleClick);
    this.refs.zoomDialog?.addEventListener('click', this.#handleZoomClick);
    this.refs.zoomDialog?.addEventListener('cancel', this.#handleZoomCancel);
    syncAddButtonsState(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('change', this.#handleSizeChange);
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('click', this.#handleClick);
    this.refs.zoomDialog?.removeEventListener('click', this.#handleZoomClick);
    this.refs.zoomDialog?.removeEventListener('cancel', this.#handleZoomCancel);
    if (this.#timeout) clearTimeout(this.#timeout);
    this.#releaseScrollLock();
  }

  /**
   * Product IDs of the trigger collection. Empty array means "trigger on any product".
   * @returns {number[]}
   */
  get #triggerProductIds() {
    const json = this.querySelector('[data-look-trigger-ids]')?.textContent;
    if (!json) return [];

    try {
      return JSON.parse(json) || [];
    } catch {
      return [];
    }
  }

  /**
   * Identifiants des produits accompagnés d'une lettre personnalisée. On
   * compare des identifiants et non des types : product_type est traduit par
   * marché (Kaftan, Caftán, قفطان) et la comparaison casserait hors FR et EN.
   * @returns {number[]}
   */
  get #letterProductIds() {
    const json = this.querySelector('[data-look-letter-ids]')?.textContent;
    if (!json) return [];

    try {
      return JSON.parse(json) || [];
    } catch {
      return [];
    }
  }

  /**
   * Les vagues de suggestions qui ont encore au moins un produit à proposer.
   * @returns {HTMLElement[]}
   */
  #availableOffers() {
    return [...this.querySelectorAll('[data-look-offer]')].filter(
      (offer) => offer instanceof HTMLElement && offer.querySelector('[data-look-product-id]:not([hidden])')
    );
  }

  /**
   * @param {CustomEvent<{ resource?: { items?: CartItem[] }, sourceId?: string, data?: { source?: string, didError?: boolean, productId?: string, variantId?: string } }>} event
   */
  #handleCartUpdate = (event) => {
    const { resource, sourceId, data } = event.detail ?? {};

    if (!data || data.didError) return;
    // Ignore les mises à jour de quantité dans le panier — seuls les ajouts comptent.
    if (data.source !== 'product-form-component') return;

    const { dialog } = this.refs;

    // Ajout déclenché depuis la pop-up elle-même : on masque le produit ajouté
    // de la grille et on laisse la fenêtre ouverte, pour permettre d'en
    // ajouter plusieurs d'affilée. On ne ferme que s'il ne reste rien.
    if (dialog.open || (event.target instanceof Node && this.contains(event.target))) {
      if (this.#timeout) clearTimeout(this.#timeout);
      this.#timeout = setTimeout(() => {
        if (hideProductsInCart(this, resource) === 0) this.closeDialog();
      }, CLOSE_AFTER_ADD_DELAY);
      return;
    }

    const addedItem = this.#findAddedItem(resource, data.variantId ?? sourceId, data.productId);

    // L'id produit peut manquer selon la source de l'ajout : on retombe alors
    // sur la ligne de panier correspondante.
    const addedProductId = Number(data.productId) || addedItem?.product_id;

    const triggerIds = this.#triggerProductIds;
    if (triggerIds.length > 0 && (!addedProductId || !triggerIds.includes(addedProductId))) return;

    const remainingOffers = hideProductsInCart(this, resource);
    const eligibleForLetter = Boolean(addedProductId && this.#letterProductIds.includes(addedProductId));

    // Rien à suggérer et rien à demander : on n'ouvre pas.
    if (remainingOffers === 0 && !eligibleForLetter) return;
    // Suggestions déjà vues et pas de question à poser : inutile d'interrompre.
    if (this.#wasShownThisSession() && !eligibleForLetter) return;

    this.#renderAddedItem(addedItem);
    this.#renderLetterField(addedItem, eligibleForLetter);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => this.#show(), OPEN_DELAY);
  };

  /**
   * Retrouve la ligne de panier du produit qui vient d'être ajouté.
   * @param {{ items?: CartItem[] } | undefined} cart
   * @param {string | undefined} variantId
   * @param {string | undefined} productId
   * @returns {CartItem | undefined}
   */
  #findAddedItem(cart, variantId, productId) {
    const items = cart?.items ?? [];
    return (
      items.find((i) => String(i.variant_id) === String(variantId)) ??
      items.find((i) => String(i.product_id) === String(productId))
    );
  }

  /**
   * Renseigne le bandeau de confirmation avec le produit qui vient d'être ajouté.
   * @param {CartItem | undefined} item
   */
  #renderAddedItem(item) {
    const { addedItem, addedItemImage, addedItemTitle, addedItemVariant } = this.refs;
    if (!addedItem || !addedItemImage || !addedItemTitle || !addedItemVariant) return;

    if (!item) {
      addedItem.hidden = true;
      return;
    }

    if (item.image) {
      addedItemImage.src = `${item.image}${item.image.includes('?') ? '&' : '?'}width=160`;
      addedItemImage.alt = item.product_title;
      addedItemImage.hidden = false;
    } else {
      addedItemImage.hidden = true;
    }

    addedItemTitle.textContent = item.product_title;
    addedItemVariant.textContent =
      item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    addedItem.hidden = false;
  }

  /**
   * Prépare le bloc lettre pour l'article qui vient d'être ajouté : le masque
   * si le produit n'y est pas éligible, sinon le vide et lui donne la ligne de
   * panier à écrire.
   *
   * À partir de la deuxième question de la session, on ne demande plus, on
   * confirme : les champs sont pré-remplis avec la dernière réponse. Une
   * cliente qui commande plusieurs caftans pour elle-même ferme sans rien
   * saisir ; celle qui commande pour sa famille voit un prénom à corriger.
   *
   * @param {CartItem | undefined} item
   * @param {boolean} eligible
   */
  #renderLetterField(item, eligible) {
    const { letterBlock } = this.refs;
    if (!letterBlock) return;

    letterBlock.hidden = !eligible || !item?.key;
    if (letterBlock.hidden) return;

    const field = letterBlock.querySelector('letter-field');
    if (!field) return;

    field.reset?.();
    field.setLineKey?.(item.key);

    const isFirstAsk = !this.#wasLetterAskedThisSession();
    letterBlock.querySelector('.letter-field__intro')?.toggleAttribute('hidden', !isFirstAsk);

    // Le libellé reste une question ouverte, jamais une question fermée : un
    // « Ce caftan aussi, pour Sarah ? » au-dessus d'un champ texte se fait
    // répondre « Oui », et la lettre part avec ce prénom-là. C'est le champ
    // pré-rempli qui porte la reconnaissance, pas le libellé.
    const previous = customElements.get('letter-field')?.lastAnswer?.() ?? null;
    if (!isFirstAsk && previous) field.prefill?.(previous.name, previous.occasion);

    const labelText = letterBlock.querySelector('.letter-field__label-text');
    const label = isFirstAsk ? field.dataset.labelFirst : field.dataset.labelNext;
    if (labelText && label) labelText.textContent = label;

    this.#markLetterAskedThisSession();
  }

  /** @param {Event} event */
  #handleSizeChange = (event) => {
    if (applySizeSelection(this, event)) syncAddButtonsState(this);
  };

  #show() {
    const { dialog, letterBlock } = this.refs;
    if (dialog.open) return;

    const offers = this.#availableOffers();
    const hasLetter = Boolean(letterBlock) && !letterBlock.hidden;
    if (offers.length === 0 && !hasLetter) return;

    // La grille de suggestions ne se montre qu'une fois par session ; le bloc
    // lettre, lui, accompagne chaque vêtement ajouté.
    const alreadyShown = this.#wasShownThisSession();
    if (offers[0]) offers[0].hidden = alreadyShown;
    if (alreadyShown && !hasLetter) return;

    syncAddButtonsState(this);
    this.#lockScroll();
    dialog.showModal();
    this.#markShownThisSession();
  }

  #lockScroll() {
    const body = document.body;
    if (body.style.position === 'fixed') return;

    this.#previousScrollY = window.scrollY;
    this.#ownsScrollLock = true;
    body.style.width = '100%';
    body.style.position = 'fixed';
    body.style.top = `-${this.#previousScrollY}px`;
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #releaseScrollLock() {
    if (!this.#ownsScrollLock) return;

    const body = document.body;
    body.style.width = '';
    body.style.position = '';
    body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    document.dispatchEvent(new Event('scroll'));
    this.#ownsScrollLock = false;
  }

  /** Fermeture de la pop-up. Branché sur ✕, Échap et clic à l'extérieur. */
  closeDialog = async () => {
    if (!this.refs.dialog.open) return;

    // Enregistre la saisie en cours avant de fermer : sans cela, un prénom
    // tapé puis une fermeture immédiate serait perdu. C'est aussi ce qui
    // valide les champs pré-remplis quand la cliente ferme sans rien toucher.
    const field = this.refs.letterBlock?.hidden === false ? this.refs.letterBlock.querySelector('letter-field') : null;
    if (field && typeof field.commit === 'function') await field.commit();

    await this.#close();
  };

  /** Fermeture réelle de la pop-up, avec son animation de sortie. */
  #close = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.classList.add('dialog-closing');
    await onAnimationEnd(dialog, undefined, { subtree: false });
    dialog.classList.remove('dialog-closing');
    dialog.close();
    this.#releaseScrollLock();
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    // Intercepte la fermeture native (Échap) : même workflow que le ✕.
    event.preventDefault();
    this.closeDialog();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    const zoomTrigger = target instanceof Element ? target.closest('[data-look-zoom]') : null;
    if (zoomTrigger) {
      this.#openZoom(zoomTrigger);
      return;
    }

    if (isClickedOutside(event, this.refs.dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Ouvre la lightbox sur l'image haute résolution de la carte cliquée.
   * @param {Element} trigger - Le bouton image (data-look-zoom).
   */
  #openZoom(trigger) {
    const { zoomDialog, zoomImage } = this.refs;
    if (!(zoomDialog instanceof HTMLDialogElement) || !(zoomImage instanceof HTMLImageElement)) return;

    const img = trigger.querySelector('img');
    const src = highResImageUrl(img instanceof HTMLImageElement ? img : null);
    if (!src) return;

    zoomImage.src = src;
    zoomImage.alt = (img instanceof HTMLImageElement && img.alt) || '';
    if (!zoomDialog.open) zoomDialog.showModal();
  }

  #closeZoom = () => {
    const { zoomDialog, zoomImage } = this.refs;
    if (!(zoomDialog instanceof HTMLDialogElement) || !zoomDialog.open) return;
    zoomDialog.close();
    if (zoomImage instanceof HTMLImageElement) zoomImage.removeAttribute('src');
  };

  /** @param {MouseEvent} event */
  #handleZoomClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // Clic sur le fond (le dialog lui-même) ou sur le bouton fermer.
    if (target === this.refs.zoomDialog || target.closest('[data-look-zoom-close]')) {
      this.#closeZoom();
    }
  };

  /** @param {Event} event */
  #handleZoomCancel = (event) => {
    // Échap ferme la lightbox sans fermer la pop-up en dessous.
    event.preventDefault();
    this.#closeZoom();
  };

  #wasShownThisSession() {
    // Dans l'éditeur de thème, on réaffiche systématiquement la pop-up pour
    // que le marchand puisse la tester à chaque ajout.
    if (window.Shopify?.designMode) return false;

    try {
      return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  #markShownThisSession() {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    } catch {
      // sessionStorage indisponible (navigation privée stricte) : la pop-up
      // pourra se réafficher, ce qui reste un comportement acceptable.
    }
  }

  #wasLetterAskedThisSession() {
    if (window.Shopify?.designMode) return false;

    try {
      return sessionStorage.getItem(LETTER_SESSION_KEY) === 'true';
    } catch {
      return false;
    }
  }

  #markLetterAskedThisSession() {
    try {
      sessionStorage.setItem(LETTER_SESSION_KEY, 'true');
    } catch {
      // sessionStorage indisponible : la phrase d'introduction se répétera,
      // ce qui reste acceptable.
    }
  }
}

if (!customElements.get('complete-look-component')) {
  customElements.define('complete-look-component', CompleteLookComponent);
}

/**
 * Section « Cross-sell produit » de la fiche produit : cartes teaser qui
 * ouvrent une fiche produit légère (description, variante, ajout panier).
 *
 * @typedef {object} InlineRefs
 * @property {HTMLDialogElement} detailDialog
 * @property {HTMLElement} detailPanel
 * @property {HTMLDialogElement} [zoomDialog]
 * @property {HTMLImageElement} [zoomImage]
 *
 * @extends {Component<InlineRefs>}
 */
class CompleteLookInlineComponent extends Component {
  requiredRefs = ['detailDialog', 'detailPanel'];

  /** @type {HTMLElement | null} */
  #activeSource = null;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {boolean} */
  #ownsScrollLock = false;

  /** @type {number | null} Y de départ d'un glissé sur la bottom-sheet mobile. */
  #dragStartY = null;

  /** @type {boolean} Le geste en cours peut-il fermer la sheet (vs scroller) ? */
  #dragAllowed = false;

  /** @type {boolean} Un glissé vers le bas est-il réellement en cours ? */
  #dragging = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('change', this.#handleSizeChange);
    this.addEventListener('click', this.#handleClick);
    this.refs.detailDialog.addEventListener('click', this.#handleDetailDialogClick);
    this.refs.detailDialog.addEventListener('cancel', this.#handleDetailCancel);
    this.refs.detailDialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.refs.detailDialog.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.refs.detailDialog.addEventListener('touchend', this.#handleTouchEnd);
    this.refs.detailDialog.addEventListener('touchcancel', this.#handleTouchEnd);
    this.refs.zoomDialog?.addEventListener('click', this.#handleZoomClick);
    this.refs.zoomDialog?.addEventListener('cancel', this.#handleZoomCancel);
    window.addEventListener('resize', this.#handleResize);
    this.#hydrateFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('change', this.#handleSizeChange);
    this.removeEventListener('click', this.#handleClick);
    this.refs.detailDialog.removeEventListener('click', this.#handleDetailDialogClick);
    this.refs.detailDialog.removeEventListener('cancel', this.#handleDetailCancel);
    this.refs.detailDialog.removeEventListener('touchstart', this.#handleTouchStart);
    this.refs.detailDialog.removeEventListener('touchmove', this.#handleTouchMove);
    this.refs.detailDialog.removeEventListener('touchend', this.#handleTouchEnd);
    this.refs.detailDialog.removeEventListener('touchcancel', this.#handleTouchEnd);
    this.refs.zoomDialog?.removeEventListener('click', this.#handleZoomClick);
    this.refs.zoomDialog?.removeEventListener('cancel', this.#handleZoomCancel);
    window.removeEventListener('resize', this.#handleResize);
    this.#returnDetailSource();
    this.#releaseScrollLock();
  }

  /** Au chargement, masque les suggestions déjà au panier. */
  async #hydrateFromCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`);
      if (!response.ok) return;
      this.#applyCart(await response.json());
    } catch {
      // Panier injoignable : on laisse toutes les suggestions visibles.
    }
  }

  /**
   * @param {CustomEvent<{ resource?: { items?: CartItem[] }, data?: { source?: string, didError?: boolean } }>} event
   */
  #handleCartUpdate = (event) => {
    const { resource, data } = event.detail ?? {};
    if (resource?.items) this.#applyCart(resource);

    if (
      data?.source === 'product-form-component' &&
      !data.didError &&
      this.refs.detailDialog.open &&
      event.target instanceof Node &&
      this.refs.detailPanel.contains(event.target)
    ) {
      setTimeout(() => this.#closeDetail(), CLOSE_AFTER_ADD_DELAY);
    }
  };

  /** @param {{ items?: CartItem[] }} cart */
  #applyCart(cart) {
    const visibleCount = hideProductsInCart(this, cart);
    this.toggleAttribute('hidden', visibleCount === 0);
  }

  /** @param {Event} event */
  #handleSizeChange = (event) => {
    const root =
      this.refs.detailDialog.open && event.target instanceof Node && this.refs.detailPanel.contains(event.target)
        ? this.refs.detailPanel
        : this;
    if (applySizeSelection(root, event)) syncAddButtonsState(root);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Lightbox ouverte par-dessus : un clic (y compris sur son fond) ne doit
    // fermer QUE la lightbox, pas la fiche en dessous. #handleZoomClick s'en
    // charge ; on ne touche pas à la fiche ici.
    if (this.refs.zoomDialog?.open) return;

    const openTrigger = target.closest('[data-look-open-detail]');
    if (openTrigger) {
      const card = openTrigger.closest('[data-look-product-id]');
      if (card instanceof HTMLElement) this.#openDetail(card);
      return;
    }

    if (target.closest('[data-look-detail-close]')) {
      this.#closeDetail();
      return;
    }

    const zoomTrigger = target.closest('[data-look-zoom]');
    if (zoomTrigger) {
      this.#openZoom(zoomTrigger);
      return;
    }

    if (this.refs.detailDialog.open && isClickedOutside(event, this.refs.detailDialog)) {
      this.#closeDetail();
    }
  };

  /** @param {Event} event */
  #handleDetailCancel = (event) => {
    event.preventDefault();
    this.#closeDetail();
  };

  /** @param {MouseEvent} event */
  #handleDetailDialogClick = (event) => {
    if (isClickedOutside(event, this.refs.detailDialog)) {
      this.#closeDetail();
    }
  };

  /**
   * @param {HTMLElement} card
   */
  #openDetail(card) {
    const productId = card.dataset.lookProductId;
    const source = productId
      ? this.querySelector(
          `.complete-look-inline__detail-store .complete-look__detail-source[data-look-product-id="${productId}"]`
        )
      : null;
    const { detailDialog, detailPanel } = this.refs;
    if (!(source instanceof HTMLElement) || detailDialog.open) return;

    if (this.#activeSource && this.#activeSource !== source) {
      this.#returnDetailSource();
    }

    this.#activeSource = source;
    source.hidden = false;
    detailPanel.appendChild(source);
    syncAddButtonsState(detailPanel);
    // Repart d'un état propre : un transform/transition résiduel d'un glissé
    // précédent (dont le nettoyage a pu être annulé par le display:none de
    // close() sur Safari) pousserait sinon la sheet hors écran à l'ouverture.
    this.#resetSheetTransform();
    this.#lockScroll();
    detailDialog.showModal();
    // Différé en double rAF : le contenu fraîchement cloné doit être mis en page
    // (et les polices appliquées) avant de mesurer sa hauteur.
    requestAnimationFrame(() => requestAnimationFrame(this.#sizeDetail));
  }

  /**
   * Desktop : la hauteur de la modale est dictée par la colonne infos (titre,
   * prix, description, variantes, CTA) pour que tout y tienne sans scroll ;
   * l'image carrée s'aligne sur cette hauteur (via --look-detail-size) et reste
   * donc aussi grande que possible sans être rognée ni laisser de vide.
   * Bornée par un plancher (évite une image minuscule) et le viewport.
   */
  /**
   * Affine la hauteur de la modale (= côté de l'image carrée) pour coller au
   * contenu de la colonne infos. Pose --look-detail-size sur le dialog ; la
   * largeur et la hauteur en découlent en CSS (cf. complete-look-inline). Pure
   * amélioration progressive : sans JS, le repli CSS reste correct.
   */
  #sizeDetail = () => {
    const dialog = this.refs.detailDialog;
    const accessory = this.refs.detailPanel.querySelector('.complete-look__detail--accessory');
    if (!(accessory instanceof HTMLElement)) return;

    // Mobile (bottom-sheet) : géré entièrement en CSS.
    if (!dialog.open || !window.matchMedia('(min-width: 750px)').matches) {
      dialog.style.removeProperty('--look-detail-size');
      return;
    }

    const content = accessory.querySelector('.complete-look__detail-content');
    const cta = accessory.querySelector('.complete-look__detail-cta');
    if (!(content instanceof HTMLElement)) return;

    // La colonne infos a une largeur fixe (flex-basis), donc sa hauteur de
    // contenu est stable : on la mesure pour en faire le côté du carré.
    const needed = content.scrollHeight + (cta instanceof HTMLElement ? cta.offsetHeight : 0);
    const floor = Math.min(window.innerHeight * 0.55, 520);
    const ceil = window.innerHeight * 0.92;
    const size = Math.round(Math.max(floor, Math.min(needed, ceil)));
    dialog.style.setProperty('--look-detail-size', `${size}px`);
  };

  #handleResize = () => {
    if (this.refs.detailDialog.open) this.#sizeDetail();
  };

  /** Mobile uniquement (la modale desktop est centrée, pas une sheet). */
  get #isSheet() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    const dialog = this.refs.detailDialog;
    if (!dialog.open || !this.#isSheet || event.touches.length !== 1) return;

    const target = event.target instanceof Element ? event.target : null;
    // Glissé autorisé si on part du handle / du hero, ou si la sheet est déjà
    // en haut de son scroll (sinon le geste sert à faire défiler le contenu).
    // C'est le dialog lui-même qui défile (cf. CSS mobile).
    const grab = target?.closest('.complete-look-inline__detail-handle, .complete-look__detail-media');
    const atTop = dialog.scrollTop <= 0;

    this.#dragAllowed = !!grab || atTop;
    this.#dragStartY = event.touches[0].clientY;
    this.#dragging = false;
  };

  /** @param {TouchEvent} event */
  #handleTouchMove = (event) => {
    if (this.#dragStartY == null || !this.#dragAllowed) return;

    const dy = event.touches[0].clientY - this.#dragStartY;
    if (dy <= 0) {
      // Geste vers le haut → ce n'est pas une fermeture : on rend la main au scroll.
      if (!this.#dragging) this.#dragAllowed = false;
      return;
    }

    this.#dragging = true;
    const dialog = this.refs.detailDialog;
    dialog.style.transition = 'none';
    // !important : l'animation d'ouverture (fill forwards) verrouille sinon
    // transform:none et écraserait notre translation.
    dialog.style.setProperty('transform', `translateY(${dy}px)`, 'important');
    event.preventDefault();
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    if (this.#dragStartY == null) return;

    const wasDragging = this.#dragging;
    const dialog = this.refs.detailDialog;
    const match = /translateY\(([-0-9.]+)px\)/.exec(dialog.style.transform);
    const dy = match ? parseFloat(match[1]) : 0;

    this.#dragStartY = null;
    this.#dragAllowed = false;
    this.#dragging = false;
    if (!wasDragging) return;

    // Évite le clic synthétisé (ex : ouverture du zoom) après un glissé.
    event.preventDefault();

    // Au-delà du seuil → fermeture ; sinon retour en place.
    if (dy > 110) {
      this.#dismissSheet();
    } else {
      dialog.style.transition = 'transform var(--animation-speed, 200ms) var(--animation-easing)';
      dialog.style.setProperty('transform', 'translateY(0px)', 'important');
      const clear = () => {
        dialog.style.transition = '';
        dialog.style.removeProperty('transform');
        dialog.removeEventListener('transitionend', clear);
      };
      dialog.addEventListener('transitionend', clear, { once: true });
      setTimeout(clear, 280);
    }
  };

  /** Ferme la sheet en la faisant glisser depuis sa position courante. */
  #dismissSheet() {
    const dialog = this.refs.detailDialog;
    let finished = false;
    const done = () => {
      if (finished) return;
      finished = true;
      dialog.removeEventListener('transitionend', done);
      dialog.style.transition = '';
      dialog.style.removeProperty('transform');
      if (dialog.open) dialog.close();
      this.#returnDetailSource();
      this.#releaseScrollLock();
    };
    dialog.style.transition = 'transform var(--animation-speed, 200ms) var(--animation-easing)';
    dialog.style.setProperty('transform', 'translateY(100%)', 'important');
    dialog.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 320);
  }

  /** Enlève tout transform/transition inline laissé par un glissé. */
  #resetSheetTransform() {
    const dialog = this.refs.detailDialog;
    dialog.style.removeProperty('transform');
    dialog.style.removeProperty('transition');
  }

  #closeDetail = async () => {
    const { detailDialog } = this.refs;
    if (!detailDialog.open) return;

    detailDialog.classList.add('dialog-closing');
    await onAnimationEnd(detailDialog, undefined, { subtree: false });
    detailDialog.classList.remove('dialog-closing');
    detailDialog.close();
    this.#resetSheetTransform();
    this.#returnDetailSource();
    this.#releaseScrollLock();
  };

  #returnDetailSource() {
    const store = this.querySelector('.complete-look-inline__detail-store');
    const source = this.refs.detailPanel.querySelector('.complete-look__detail-source');
    if (!(source instanceof HTMLElement) || !(store instanceof HTMLElement)) return;

    source.hidden = true;
    store.appendChild(source);
    this.#activeSource = null;
  }

  #lockScroll() {
    const body = document.body;
    if (body.style.position === 'fixed') return;

    this.#previousScrollY = window.scrollY;
    this.#ownsScrollLock = true;
    body.style.width = '100%';
    body.style.position = 'fixed';
    body.style.top = `-${this.#previousScrollY}px`;
    document.documentElement.setAttribute('scroll-lock', '');
  }

  #releaseScrollLock() {
    if (!this.#ownsScrollLock) return;

    const body = document.body;
    body.style.width = '';
    body.style.position = '';
    body.style.top = '';
    document.documentElement.removeAttribute('scroll-lock');
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    document.dispatchEvent(new Event('scroll'));
    this.#ownsScrollLock = false;
  }

  /**
   * @param {Element} trigger
   */
  #openZoom(trigger) {
    const { zoomDialog, zoomImage } = this.refs;
    if (!(zoomDialog instanceof HTMLDialogElement) || !(zoomImage instanceof HTMLImageElement)) return;

    const img = trigger.querySelector('img');
    const src = highResImageUrl(img instanceof HTMLImageElement ? img : null);
    if (!src) return;

    zoomImage.src = src;
    zoomImage.alt = (img instanceof HTMLImageElement && img.alt) || '';
    if (!zoomDialog.open) zoomDialog.showModal();
  }

  #closeZoom = () => {
    const { zoomDialog, zoomImage } = this.refs;
    if (!(zoomDialog instanceof HTMLDialogElement) || !zoomDialog.open) return;
    zoomDialog.close();
    if (zoomImage instanceof HTMLImageElement) zoomImage.removeAttribute('src');
  };

  /** @param {MouseEvent} event */
  #handleZoomClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // La lightbox est au-dessus : son clic ne doit pas remonter jusqu'à
    // #handleClick (qui fermerait la fiche en dessous). On ne ferme que le zoom.
    event.stopPropagation();
    if (target === this.refs.zoomDialog || target.closest('[data-look-zoom-close]')) {
      this.#closeZoom();
    }
  };

  /** @param {Event} event */
  #handleZoomCancel = (event) => {
    event.preventDefault();
    this.#closeZoom();
  };
}

if (!customElements.get('complete-look-inline-component')) {
  customElements.define('complete-look-inline-component', CompleteLookInlineComponent);
}
